import * as THREE from 'three/webgpu';
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import { TERRAIN, heightAt, slopeAt, trackQuery } from '../terrain/heightfield.js';

export function setupLighting(scene) {
  // Sunlight
  const sunLight = new THREE.DirectionalLight(0xfff3db, 2.2);
  sunLight.position.set(240, 360, 180);
  sunLight.castShadow = true;
  sunLight.shadow.mapSize.width = 2048;
  sunLight.shadow.mapSize.height = 2048;
  sunLight.shadow.camera.near = 10;
  sunLight.shadow.camera.far = 700;
  sunLight.shadow.camera.left = -260;
  sunLight.shadow.camera.right = 260;
  sunLight.shadow.camera.top = 260;
  sunLight.shadow.camera.bottom = -260;
  sunLight.shadow.bias = -0.0005;
  scene.add(sunLight);

  // Hemisphere ambient
  const hemiLight = new THREE.HemisphereLight(0xbfdbfe, 0x3b2e21, 0.95);
  scene.add(hemiLight);

  // Ambient fill
  const ambient = new THREE.AmbientLight(0xffedd5, 0.35);
  scene.add(ambient);

  // Sky dome
  const sky = new SkyMesh();
  sky.scale.setScalar(450000);
  sky.turbidity.value = 2.4;
  sky.rayleigh.value = 1.15;
  sky.mieCoefficient.value = 0.0045;
  sky.sunPosition.value.copy(sunLight.position);
  scene.add(sky);

  return { sunLight, hemiLight, sky };
}

export function createWater(scene) {
  const waterGeom = new THREE.CircleGeometry(TERRAIN.islandEnd * 1.35, 64);
  waterGeom.rotateX(-Math.PI / 2);
  const waterMat = new THREE.MeshStandardNodeMaterial({
    color: 0x1d4e68,
    roughness: 0.12,
    metalness: 0.45,
    transparent: true,
    opacity: 0.88
  });
  const waterMesh = new THREE.Mesh(waterGeom, waterMat);
  waterMesh.position.y = -2.2;
  waterMesh.receiveShadow = true;
  scene.add(waterMesh);
  return waterMesh;
}

export function buildScenery(scene, checkpoints) {
  // Deterministic PRNG
  let seed = 1337;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };

  const dummy = new THREE.Object3D();

  // 1. Rocks / Boulders (InstancedMesh)
  const ROCK_COUNT = 650;
  const rockGeom = new THREE.DodecahedronGeometry(1.4, 0);
  const rockMat = new THREE.MeshStandardNodeMaterial({
    color: 0x695f54,
    roughness: 0.92,
    metalness: 0.08
  });
  const rockMesh = new THREE.InstancedMesh(rockGeom, rockMat, ROCK_COUNT);
  rockMesh.castShadow = true;
  rockMesh.receiveShadow = true;

  let rockIdx = 0;
  for (let attempt = 0; attempt < 4000 && rockIdx < ROCK_COUNT; attempt++) {
    const r = Math.sqrt(rnd()) * (TERRAIN.islandStart - 20);
    const theta = rnd() * Math.PI * 2;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    const q = trackQuery(x, z);
    // Don't place on track roadbed; allow flanking along shoulders and mountains
    if (q.dist < TERRAIN.roadHalfWidth + 1.8) continue;

    const h = heightAt(x, z);
    const scale = 0.7 + rnd() * 2.2;
    dummy.position.set(x, h + scale * 0.45, z);
    dummy.rotation.set(rnd() * Math.PI, rnd() * Math.PI, rnd() * Math.PI);
    dummy.scale.set(scale, scale * (0.8 + rnd() * 0.4), scale);
    dummy.updateMatrix();
    rockMesh.setMatrixAt(rockIdx, dummy.matrix);

    const rockTint = new THREE.Color().setHSL(0.08 + rnd() * 0.05, 0.12, 0.32 + rnd() * 0.18);
    rockMesh.setColorAt(rockIdx, rockTint);
    rockIdx++;
  }
  rockMesh.instanceMatrix.needsUpdate = true;
  if (rockMesh.instanceColor) rockMesh.instanceColor.needsUpdate = true;
  scene.add(rockMesh);

  // 2. Bushes / Desert Scrub (InstancedMesh)
  const BUSH_COUNT = 750;
  const bushGeom = new THREE.DodecahedronGeometry(1.0, 1);
  const bushMat = new THREE.MeshStandardNodeMaterial({
    color: 0x475932,
    roughness: 0.88,
    metalness: 0.02
  });
  const bushMesh = new THREE.InstancedMesh(bushGeom, bushMat, BUSH_COUNT);
  bushMesh.castShadow = true;
  bushMesh.receiveShadow = true;

  let bushIdx = 0;
  for (let attempt = 0; attempt < 5000 && bushIdx < BUSH_COUNT; attempt++) {
    const r = Math.sqrt(rnd()) * (TERRAIN.islandStart - 35);
    const theta = rnd() * Math.PI * 2;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    const q = trackQuery(x, z);
    if (q.dist < TERRAIN.roadHalfWidth + 2.5) continue;

    const h = heightAt(x, z);
    const sl = slopeAt(x, z);
    if (sl > 0.45 || h > 42 || h < 2) continue;

    const scale = 0.8 + rnd() * 1.4;
    dummy.position.set(x, h + scale * 0.6, z);
    dummy.rotation.set(0, rnd() * Math.PI * 2, 0);
    dummy.scale.set(scale * (0.9 + rnd() * 0.3), scale * (0.7 + rnd() * 0.3), scale * (0.9 + rnd() * 0.3));
    dummy.updateMatrix();
    bushMesh.setMatrixAt(bushIdx, dummy.matrix);

    const bushTint = new THREE.Color().setHSL(0.24 + rnd() * 0.08, 0.35, 0.28 + rnd() * 0.14);
    bushMesh.setColorAt(bushIdx, bushTint);
    bushIdx++;
  }
  bushMesh.instanceMatrix.needsUpdate = true;
  if (bushMesh.instanceColor) bushMesh.instanceColor.needsUpdate = true;
  scene.add(bushMesh);

  // 3. Desert Acacia / Trees (InstancedMesh for Trunks & Canopies)
  const TREE_COUNT = 320;
  const trunkGeom = new THREE.CylinderGeometry(0.35, 0.55, 4.5, 7);
  trunkGeom.translate(0, 2.25, 0);
  const canopyGeom = new THREE.ConeGeometry(3.0, 3.8, 7);
  canopyGeom.translate(0, 5.8, 0);

  const trunkMat = new THREE.MeshStandardNodeMaterial({ color: 0x433221, roughness: 0.9 });
  const canopyMat = new THREE.MeshStandardNodeMaterial({ color: 0x2e4624, roughness: 0.85 });

  const treeTrunkMesh = new THREE.InstancedMesh(trunkGeom, trunkMat, TREE_COUNT);
  const treeCanopyMesh = new THREE.InstancedMesh(canopyGeom, canopyMat, TREE_COUNT);
  treeTrunkMesh.castShadow = true;
  treeCanopyMesh.castShadow = true;
  treeCanopyMesh.receiveShadow = true;

  let treeIdx = 0;
  for (let attempt = 0; attempt < 5000 && treeIdx < TREE_COUNT; attempt++) {
    const r = Math.sqrt(rnd()) * (TERRAIN.islandStart - 45);
    const theta = rnd() * Math.PI * 2;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    const q = trackQuery(x, z);
    if (q.dist < TERRAIN.roadHalfWidth + 4.5) continue;

    const h = heightAt(x, z);
    const sl = slopeAt(x, z);
    if (sl > 0.35 || h > 34 || h < 2.5) continue;

    const scale = 1.0 + rnd() * 1.5;
    dummy.position.set(x, h, z);
    dummy.rotation.set(0, rnd() * Math.PI * 2, 0);
    dummy.scale.set(scale, scale, scale);
    dummy.updateMatrix();

    treeTrunkMesh.setMatrixAt(treeIdx, dummy.matrix);
    treeCanopyMesh.setMatrixAt(treeIdx, dummy.matrix);

    const canopyTint = new THREE.Color().setHSL(0.26 + rnd() * 0.06, 0.38, 0.22 + rnd() * 0.12);
    treeCanopyMesh.setColorAt(treeIdx, canopyTint);
    treeIdx++;
  }
  treeTrunkMesh.instanceMatrix.needsUpdate = true;
  treeCanopyMesh.instanceMatrix.needsUpdate = true;
  if (treeCanopyMesh.instanceColor) treeCanopyMesh.instanceColor.needsUpdate = true;
  scene.add(treeTrunkMesh);
  scene.add(treeCanopyMesh);

  // 4. Checkpoints (InstancedMesh for Pylons + Crossbars, glowing gate rings)
  const cpCount = checkpoints.length;
  const PYLON_TOTAL = cpCount * 2;
  const pylonGeom = new THREE.CylinderGeometry(0.5, 0.7, 8.5, 8);
  pylonGeom.translate(0, 4.25, 0);
  const pylonMat = new THREE.MeshStandardNodeMaterial({
    color: 0x1f2937,
    roughness: 0.4,
    metalness: 0.8
  });
  const pylonMesh = new THREE.InstancedMesh(pylonGeom, pylonMat, PYLON_TOTAL);
  pylonMesh.castShadow = true;
  pylonMesh.receiveShadow = true;

  const barGeom = new THREE.BoxGeometry(22, 0.8, 1.2);
  const barMat = new THREE.MeshStandardNodeMaterial({
    color: 0x334155,
    roughness: 0.3,
    metalness: 0.85
  });
  const barMesh = new THREE.InstancedMesh(barGeom, barMat, cpCount);
  barMesh.castShadow = true;

  const gateRings = [];

  for (let i = 0; i < cpCount; i++) {
    const cp = checkpoints[i];
    const sinY = Math.sin(cp.yaw);
    const cosY = Math.cos(cp.yaw);

    // Left and right pylon offsets (perpendicular to track direction)
    const span = 10.2;
    const lx = cp.x - cosY * span;
    const lz = cp.z + sinY * span;
    const rx = cp.x + cosY * span;
    const rz = cp.z - sinY * span;

    const lh = heightAt(lx, lz);
    const rh = heightAt(rx, rz);

    // Left pylon
    dummy.position.set(lx, lh, lz);
    dummy.rotation.set(0, cp.yaw, 0);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    pylonMesh.setMatrixAt(i * 2 + 0, dummy.matrix);

    // Right pylon
    dummy.position.set(rx, rh, rz);
    dummy.rotation.set(0, cp.yaw, 0);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    pylonMesh.setMatrixAt(i * 2 + 1, dummy.matrix);

    // Crossbar arch overhead
    const avgH = Math.max(lh, rh) + 7.5;
    dummy.position.set(cp.x, avgH, cp.z);
    dummy.rotation.set(0, cp.yaw, 0);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    barMesh.setMatrixAt(i, dummy.matrix);

    // Glowing gate ring / holographic arch
    const ringGeom = new THREE.TorusGeometry(7.2, 0.22, 8, 24, Math.PI);
    const ringMat = new THREE.MeshBasicNodeMaterial({
      color: i === 0 ? 0x10b981 : 0xf59e0b,
      transparent: true,
      opacity: 0.85
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.position.set(cp.x, cp.groundHeight + 0.2, cp.z);
    ring.rotation.set(0, cp.yaw, 0);
    scene.add(ring);
    gateRings.push(ring);
  }

  pylonMesh.instanceMatrix.needsUpdate = true;
  barMesh.instanceMatrix.needsUpdate = true;
  scene.add(pylonMesh);
  scene.add(barMesh);

  return {
    rockMesh,
    bushMesh,
    treeTrunkMesh,
    treeCanopyMesh,
    pylonMesh,
    barMesh,
    gateRings
  };
}
